/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Announced } from '@fluentui/react/lib/Announced';
import { Stack } from '@fluentui/react/lib/Stack';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useIntents } from 'src/luis/modules/intents/useIntents';
import { TestBladeActionBar } from 'src/luis/projects/testPane/components/common/TestBladeActionBar';
import { getTestPaneTopAction } from 'src/luis/projects/testPane/components/common/TestPaneTopAction';
import { UtteranceInspector } from 'src/luis/projects/testPane/components/UtteranceInspector/UtteranceInspector';
import { UtteranceCompositeEntityStore } from 'src/luis/projects/testPane/types/types';
import { getTopScoringIntent } from 'src/luis/projects/testPane/utils/getTopScoringIntent';

export type DebugBladeProps = {
    /**
     * The prediction test result item
     */
    selectedUtteranceId: React.ReactText;

    /**
     * Fires when on compare published action clicked
     */
    onComparePublished: () => void;

    /**
     * Called when the user starts over.
     */
    onStartOver: () => void;

    /**
     * Header to be announced by screen reader.
     */
    bladeHeader: string;
};

const messages = defineMessages({
    compareWithPublished: {
        id: 'testPane.debugBlade.compareWithPublished',
        defaultMessage: 'Compare with published'
    },
    startOver: {
        id: 'testPane.debugBlade.startOver',
        defaultMessage: 'Start over'
    }
});

const CompareAction = getTestPaneTopAction('right', 'light');
const StartOverAction = getTestPaneTopAction('left', 'light');

export const DebugBlade = observer((props: DebugBladeProps) => {
    const { selectedUtteranceId, onComparePublished, onStartOver, bladeHeader } = props;
    const { dispatcher, rootStore } = useGlobalState();
    const { intents } = useIntents();
    const { localize } = useLocalization();

    const predictionStoreHouse = rootStore.currentLuisAppStore.currentAppVersionStore.testPanePredictionStoreHouse;
    const selectedUtterance = predictionStoreHouse.findById(selectedUtteranceId);

    const [selectedIntent, setSelectedIntent] = React.useState(
        getTopScoringIntent(selectedUtterance.mainUtterance.utterance.predictedIntents)
    );

    React.useEffect(() => {
        setSelectedIntent(getTopScoringIntent(selectedUtterance.mainUtterance.utterance.predictedIntents));
    }, [selectedUtterance]);

    /**
     * Clears the prediction store house test utterances.
     */
    const onStartOverClick = () => {
        dispatcher.dispatch('clearTestUtterances');
        onStartOver();
    };

    return (
        <div>
            <Announced message={bladeHeader} />

            <TestBladeActionBar>
                <Stack horizontal horizontalAlign="space-between">
                    <StartOverAction data-automation-id="startOverButton" onClick={onStartOverClick}>
                        {localize(messages.startOver)}
                    </StartOverAction>

                    <CompareAction onClick={onComparePublished}>{localize(messages.compareWithPublished)}</CompareAction>
                </Stack>
            </TestBladeActionBar>

            <UtteranceInspector
                allIntents={intents}
                selectedIntent={selectedIntent}
                predictedEntities={selectedUtterance.mainUtterance.entities}
                utteranceText={selectedUtterance.mainUtterance.utterance.text}
                sentiment={selectedUtterance.mainUtterance.utterance.sentiment}
                predictedIntents={selectedUtterance.mainUtterance.utterance.predictedIntents}
                predictedPatterns={selectedUtterance.mainUtterance.utterance.predictedPatterns}
                predictedNDepthEntities={selectedUtterance.mainUtterance.nDepths as UtteranceCompositeEntityStore[]}
                predictedCompositeEntities={selectedUtterance.mainUtterance.composites as UtteranceCompositeEntityStore[]}
            />
        </div>
    );
});
